// src/features/dashboard/components/ProductivityChart.tsx

import { useState } from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';

import {
  useProductivityChart,
} from '../hooks/useProductivityChart';

import type {
  CustomTooltipProps,
  ProductivityChartProps, // ✅ ADD THIS
} from '../types/productivityChart.types';

import {
  getLabel,
  labelStyle,
} from '../utils/productivityChart.utils';

/**
 * Custom Tooltip
 */
const CustomTooltip = ({
  active,
  payload,
  label,
}: CustomTooltipProps) => {

  if (
    active &&
    payload &&
    payload.length
  ) {

    const current = payload[0];

    return (
      <div
        className='
          rounded-[12px]
          border
          border-[#E5E7EB]
          bg-white
          shadow-2xl
        '
      >

        {/* Header */}
        <div
          className='
            border-b
            border-[#EEF2F7]

            px-3
            py-2

            text-[12px]
            font-semibold
            text-[#667085]
          '
        >
          {label?.replace('\n', ' ')}
        </div>

        {/* Body */}
        <div
          className='
            flex
            items-center
            gap-2

            px-3
            py-3
          '
        >

          <div
            className='
              h-[10px]
              w-[10px]
              rounded-full
            '
            style={{
              backgroundColor:
                current.color,
            }}
          />

          <span
            className='
              text-[12px]
              text-[#4B5563]
            '
          >
            {getLabel(
              current.dataKey,
            )}
          </span>

          <span
            className='
              ml-auto
              text-[12px]
              font-bold
              text-[#111827]
            '
          >
            {current.value}%
          </span>
        </div>
      </div>
    );
  }

  return null;
};

const ProductivityChart = ({
  ManagerEmployeeCode,
}: ProductivityChartProps) => {

  const [
    activeLegend,
    setActiveLegend,
  ] = useState<string | null>(
    null,
  );

  /**
   * Hook
   */
  const {
  chartData,
  loading,
  error,
} = useProductivityChart(ManagerEmployeeCode);

  /**
   * Loading
   */
  if (loading) {

    return (
      <div
        className='
          flex
          h-[260px]
          items-center
          justify-center

          rounded-[18px]
          border
          border-[#E5E7EB]
          bg-white

          text-[14px]
          text-[#667085]
        '
      >
        Loading chart...
      </div>
    );
  }

  /**
   * Section Error Only
   */
  if (error) {

    return (
      <div
        className='
          flex
          h-[260px]
          flex-col
          items-center
          justify-center

          rounded-[18px]
          border
          border-[#FECACA]
          bg-[#FEF2F2]

          text-center
        '
      >

        <h3
          className='
            text-[14px]
            font-semibold
            text-[#DC2626]
          '
        >
          Productivity Chart Error
        </h3>

        <p
          className='
            mt-1
            text-[12px]
            text-[#7F1D1D]
          '
        >
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className='bg-white'>

      {/* Header */}
      <div
        className='
          mb-5
          flex
          items-center
          gap-3
        '
      >

        <h2
          className='
            text-[15px]
            font-bold
            text-[#002B5B]
          '
        >
          Team Productivity Comparison
        </h2>

        <div
          className='
            rounded-full
            bg-[#EDF4FF]

            px-3
            py-[5px]

            text-[10px]
            font-medium
            text-[#2563EB]
          '
        >
          Split across all 4 teams
        </div>
      </div>

      {/* Chart */}
      <div className='h-[260px]'>

        <ResponsiveContainer
          width='100%'
          height='100%'
        >

          <BarChart
            data={chartData}
            barCategoryGap={38}
            margin={{
              top: 8,
              right: 10,
              left: -20,
              bottom: 8,
            }}
          >

            {/* Grid */}
            <CartesianGrid
              vertical={false}
              stroke='#E5E7EB'
            />

            {/* X Axis */}
            <XAxis
              dataKey='name'
              tick={{
                fontSize: 11,
                fill: '#475467',
              }}
              axisLine={{
                stroke: '#D0D5DD',
              }}
              tickLine={false}
            />

            {/* Y Axis */}
            <YAxis
              domain={[0, 100]}
              ticks={[
                0,
                20,
                40,
                60,
                80,
                100,
              ]}
              tick={{
                fontSize: 11,
                fill: '#475467',
              }}
              axisLine={false}
              tickLine={false}
            />

            {/* Tooltip */}
            <Tooltip
              shared={false}
              cursor={{
                fill:
                  'rgba(15,23,42,0.04)',
              }}
              content={
                <CustomTooltip />
              }
            />

            {/* Legend */}
            <Legend
              content={() => (

                <div
                  className='
                    mt-5
                    flex
                    items-center
                    justify-center
                    gap-5

                    text-[12px]
                  '
                >

                  {/* Productive */}
                  <div
                    onMouseEnter={() =>
                      setActiveLegend(
                        'productive',
                      )
                    }
                    onMouseLeave={() =>
                      setActiveLegend(
                        null,
                      )
                    }
                    className='
                      flex
                      cursor-pointer
                      items-center
                      gap-2
                    '
                  >

                    <div
                      className={`
                        h-[12px]
                        w-[12px]
                        rounded-[2px]

                        ${
                          activeLegend ===
                          'productive'
                            ? 'bg-[#10B981]'
                            : 'bg-[#14C8A8]'
                        }
                      `}
                    />

                    <span
                      className={`
                        transition-all
                        duration-200

                        ${
                          activeLegend ===
                          'productive'
                            ? 'font-semibold text-[#111827]'
                            : 'text-[#475467]'
                        }
                      `}
                    >
                      Productive %
                    </span>
                  </div>

                  {/* Neutral */}
                  <div
                    onMouseEnter={() =>
                      setActiveLegend(
                        'neutral',
                      )
                    }
                    onMouseLeave={() =>
                      setActiveLegend(
                        null,
                      )
                    }
                    className='
                      flex
                      cursor-pointer
                      items-center
                      gap-2
                    '
                  >

                    <div
                      className={`
                        h-[12px]
                        w-[12px]
                        rounded-[2px]

                        ${
                          activeLegend ===
                          'neutral'
                            ? 'bg-[#2563EB]'
                            : 'bg-[#3B82F6]'
                        }
                      `}
                    />

                    <span
                      className={`
                        transition-all
                        duration-200

                        ${
                          activeLegend ===
                          'neutral'
                            ? 'font-semibold text-[#111827]'
                            : 'text-[#475467]'
                        }
                      `}
                    >
                      Neutral %
                    </span>
                  </div>

                  {/* Idle */}
                  <div
                    onMouseEnter={() =>
                      setActiveLegend(
                        'idle',
                      )
                    }
                    onMouseLeave={() =>
                      setActiveLegend(
                        null,
                      )
                    }
                    className='
                      flex
                      cursor-pointer
                      items-center
                      gap-2
                    '
                  >

                    <div
                      className={`
                        h-[12px]
                        w-[12px]
                        rounded-[2px]

                        ${
                          activeLegend ===
                          'idle'
                            ? 'bg-[#F59E0B]'
                            : 'bg-[#F5A623]'
                        }
                      `}
                    />

                    <span
                      className={`
                        transition-all
                        duration-200

                        ${
                          activeLegend ===
                          'idle'
                            ? 'font-semibold text-[#111827]'
                            : 'text-[#475467]'
                        }
                      `}
                    >
                      Idle / Away %
                    </span>
                  </div>
                </div>
              )}
            />

            {/* Productive */}
            <Bar
              dataKey='productive'
              stackId='a'
              barSize={50}
              animationDuration={900}
              animationBegin={0}
              fill={
                !activeLegend ||
                activeLegend ===
                  'productive'
                  ? '#14C8A8'
                  : '#D1D5DB'
              }
            >

              <LabelList
                dataKey='productive'
                position='center'
                formatter={(value) =>
                  `${value}%`
                }
                style={labelStyle}
              />
            </Bar>

            {/* Neutral */}
            <Bar
              dataKey='neutral'
              stackId='a'
              barSize={50}
              animationDuration={1100}
              animationBegin={150}
              fill={
                !activeLegend ||
                activeLegend ===
                  'neutral'
                  ? '#3B82F6'
                  : '#D1D5DB'
              }
            >

              <LabelList
                dataKey='neutral'
                position='center'
                formatter={(value) =>
                  `${value}%`
                }
                style={labelStyle}
              />
            </Bar>

            {/* Idle */}
            <Bar
              dataKey='idle'
              stackId='a'
              radius={[4, 4, 0, 0]}
              barSize={50}
              animationDuration={1300}
              animationBegin={300}
              fill={
                !activeLegend ||
                activeLegend ===
                  'idle'
                  ? '#F5A623'
                  : '#D1D5DB'
              }
            >

              <LabelList
                dataKey='idle'
                position='center'
                formatter={(value) =>
                  `${value}%`
                }
                style={labelStyle}
              />
            </Bar>

          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ProductivityChart;